import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, CheckCircle, XCircle, Loader2, MapPin, Users, Car } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import Layout from "@/components/Layout";

// PayPal SDK loaded dynamically

interface PaymentState {
  bookingId: string;
  bookingCode: string;
  from: string;
  to: string;
  passengers: number;
  routeType: string;
  totalPrice: number;
  pickupDate: string;
  returnDate: string | null;
  distanceKm: number | null;
  durationMin: number | null;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  flightNumber: string | null;
  specialRequests: string | null;
  paymentMethod: string;
  vehicle: {
    name: string;
    image_url: string | null;
  };
}

type PaymentStatus = "loading" | "ready" | "processing" | "success" | "cancelled" | "error";

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as PaymentState | null;

  const [status, setStatus] = useState<PaymentStatus>("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const paypalContainerRef = useRef<HTMLDivElement>(null);
  const buttonsRendered = useRef(false);

  const totalPrice = state?.totalPrice ?? 0;

  useEffect(() => {
    if (!state) return;

    let scriptEl: HTMLScriptElement | null = null;

    const loadPayPal = async () => {
      const { data: paypalConfig, error: paypalError } = await supabase.functions.invoke("get-paypal-client-id");

      if (paypalError || !paypalConfig?.success || !paypalConfig?.client_id) {
        setStatus("error");
        setErrorMsg("PayPal is not configured. Please contact support.");
        return;
      }

      const pm = { client_id: paypalConfig.client_id };

      const existing = document.querySelector('script[src*="paypal.com/sdk/js"]');
      if (existing) existing.remove();

      scriptEl = document.createElement("script");
      scriptEl.src = `https://www.paypal.com/sdk/js?client-id=${(pm as any).client_id}&currency=USD`;
      scriptEl.async = true;
      scriptEl.onload = () => {
        if (!buttonsRendered.current && window.paypal && paypalContainerRef.current) {
          buttonsRendered.current = true;
          renderButtons();
        }
      };
      scriptEl.onerror = () => {
        setStatus("error");
        setErrorMsg("Failed to load PayPal. Please try again.");
      };
      document.body.appendChild(scriptEl);
    };

    const renderButtons = () => {
      setStatus("ready");

      window.paypal.Buttons({
        style: { layout: "vertical", shape: "rect", label: "pay" },
        createOrder: (_data: any, actions: any) => {
          return actions.order.create({
            purchase_units: [{
              description: `Transfer Booking ${state.bookingCode}`,
              amount: { value: totalPrice.toFixed(2), currency_code: "USD" },
            }],
          });
        },
        onApprove: async (_data: any, actions: any) => {
          setStatus("processing");
          try {
            const details = await actions.order.capture();

            const { error } = await supabase
              .from("shuttle_bookings")
              .update({
                booking_status: "confirmed",
                payment_status: "completed",
                payment_id: details.id,
              } as any)
              .eq("id", state.bookingId);

            if (error) {
              console.error(error);
              setStatus("error");
              setErrorMsg("Payment captured but failed to update booking. Please contact support.");
              return;
            }

            setStatus("success");
            toast.success("Payment successful!");

            // Send confirmation email (fire-and-forget)
            supabase.functions.invoke("send-shuttle-booking-email", {
              body: { booking_id: state.bookingId },
            }).catch(console.error);

            setTimeout(() => {
              navigate("/shuttle/confirmation", {
                state: {
                  booking: {
                    id: state.bookingId,
                    booking_code: state.bookingCode,
                    pickup_location: state.from,
                    dropoff_location: state.to,
                    passengers: state.passengers,
                    route_type: state.routeType,
                    total_price: state.totalPrice,
                    arrival_date: state.pickupDate,
                    return_date: state.returnDate,
                    distance_km: state.distanceKm,
                    estimated_time_min: state.durationMin,
                    full_name: state.customerName,
                    email: state.customerEmail,
                    phone: state.customerPhone,
                    flight_number: state.flightNumber,
                    remarks: state.specialRequests,
                    payment_method: "paypal",
                    payment_status: "completed",
                    booking_status: "confirmed",
                    payment_id: details.id,
                  },
                  vehicle: state.vehicle,
                },
              });
            }, 1500);
          } catch (err) {
            console.error(err);
            setStatus("error");
            setErrorMsg("An error occurred capturing the payment.");
          }
        },
        onCancel: () => setStatus("cancelled"),
        onError: (err: any) => {
          console.error("PayPal error:", err);
          setStatus("error");
          setErrorMsg("PayPal encountered an error. Please try again.");
        },
      }).render(paypalContainerRef.current);
    };

    loadPayPal();

    return () => {
      if (scriptEl) scriptEl.remove();
      buttonsRendered.current = false;
    };
  }, [state]);

  if (!state) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center space-y-4">
            <p className="text-muted-foreground">No payment information found.</p>
            <Button onClick={() => navigate("/")}>Go Home</Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              Complete Your <span className="text-primary">Payment</span>
            </h1>
            <p className="text-muted-foreground">Secure payment powered by PayPal</p>
          </div>

          {/* Summary Card */}
          <div className="p-6 rounded-2xl bg-card border border-border space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Booking Code</p>
                <p className="font-bold text-foreground text-lg">{state.bookingCode}</p>
              </div>
              <Badge variant="secondary" className="text-xs">
                {state.routeType === "round-trip" || state.routeType === "roundtrip" ? "Round Trip" : "One Way"}
              </Badge>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Pick-up</p>
                  <p className="text-foreground font-medium">{state.from}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-accent mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Drop-off</p>
                  <p className="text-foreground font-medium">{state.to}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">{state.passengers} passenger(s)</span>
              </div>
              <div className="flex items-center gap-2">
                <Car className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">{state.vehicle.name}</span>
              </div>
            </div>

            <div className="pt-4 border-t border-border flex items-end justify-between">
              <p className="text-muted-foreground text-sm">Total</p>
              <p className="text-3xl font-bold text-primary">${totalPrice.toFixed(2)}</p>
            </div>
          </div>

          {/* Payment Section */}
          {status === "success" ? (
            <div className="p-8 rounded-2xl bg-card border border-border text-center space-y-4">
              <CheckCircle className="w-16 h-16 text-primary mx-auto" />
              <h2 className="text-xl font-bold text-foreground">Payment Successful!</h2>
              <p className="text-muted-foreground">Redirecting to your confirmation...</p>
            </div>
          ) : status === "cancelled" ? (
            <div className="p-8 rounded-2xl bg-card border border-border text-center space-y-4">
              <XCircle className="w-16 h-16 text-accent mx-auto" />
              <h2 className="text-xl font-bold text-foreground">Payment Cancelled</h2>
              <p className="text-muted-foreground">Your booking is still saved. You can try again.</p>
              <div className="flex gap-3 justify-center">
                <Button onClick={() => { setStatus("ready"); buttonsRendered.current = false; window.location.reload(); }}>Try Again</Button>
                <Button variant="outline" onClick={() => navigate("/")}>Go Home</Button>
              </div>
            </div>
          ) : status === "error" ? (
            <div className="p-8 rounded-2xl bg-card border border-border text-center space-y-4">
              <XCircle className="w-16 h-16 text-destructive mx-auto" />
              <h2 className="text-xl font-bold text-foreground">Payment Error</h2>
              <p className="text-muted-foreground">{errorMsg}</p>
              <Button variant="outline" onClick={() => navigate("/")}>Go Home</Button>
            </div>
          ) : (
            <div className="p-6 rounded-2xl bg-card border border-border space-y-4">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-bold text-foreground">Choose Payment Method</h2>
              </div>
              {(status === "loading" || status === "processing") && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  <span className="ml-3 text-muted-foreground">
                    {status === "processing" ? "Processing payment..." : "Loading PayPal..."}
                  </span>
                </div>
              )}
              <div ref={paypalContainerRef} className={status === "loading" || status === "processing" ? "hidden" : ""} />
            </div>
          )}

          {status !== "success" && (
            <p className="text-center text-xs text-muted-foreground">
              🔒 Your payment is processed securely by PayPal. We never store your card details.
            </p>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default PaymentPage;